import styled from '@emotion/styled';
import Image from 'next/image';
import { hideVisually } from 'polished';

type BayVisibility = 'xs' | 'md' | 'lg';

/*
 * All sizes come from the bay width in `--bay`, so the car park scales as one
 * unit. The factors are the proportions of the Figma design (bay width 112px).
 */
/** Introduces the list of rental companies for screen readers. */
const CarParkIntro = styled.p`
  ${hideVisually()};
`;

const CarPark = styled.ul`
  --bay: 3.35rem;

  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
  justify-content: center;
  align-items: flex-end;

  ${({ theme }) => theme.mq('md')} {
    --bay: 5.4rem;
  }

  ${({ theme }) => theme.mq('lg')} {
    --bay: 7rem;
  }
`;

const Bay = styled('li', {
  shouldForwardProp: prop => prop !== 'visibleFrom',
})<{ visibleFrom: BayVisibility }>`
  display: ${({ visibleFrom }) => (visibleFrom === 'xs' ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  gap: calc(var(--bay) * 0.036);
  width: var(--bay);

  ${({ theme, visibleFrom }) =>
    visibleFrom !== 'xs' &&
    `
    ${theme.mq(visibleFrom)} {
      display: flex;
    }
  `}
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  height: calc(var(--bay) * 0.2);
`;

/* The logo keeps its size relative to the 112px bay of the design. */
const Logo = styled(Image, {
  shouldForwardProp: prop => prop !== '$designWidth',
})<{ $designWidth: number }>`
  width: calc(var(--bay) * ${({ $designWidth }) => $designWidth / 112});
  height: auto;
`;

const ParkingSpace = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: calc(var(--bay) * 1.607);
`;

const BayLines = styled(Image, {
  shouldForwardProp: prop => prop !== '$flipped',
})<{ $flipped?: boolean }>`
  flex: none;
  width: calc(var(--bay) * 1.027);
  height: auto;
  transform: ${({ $flipped }) => ($flipped ? 'scaleY(-1)' : 'none')};
`;

const Car = styled(Image)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(var(--bay) * 0.78);
  height: auto;
  transform: translate(-50%, -50%);
`;

export {
  Bay,
  BayLines,
  Car,
  CarPark,
  CarParkIntro,
  Logo,
  LogoRow,
  ParkingSpace,
};
export type { BayVisibility };
